// CSRF helpers for portal-kit fetch-using components.
//
// Contract with the host page:
//   <meta name="csrf-token" content="...">
//   <meta name="csrf-header" content="RequestVerificationToken"> (optional)
//
// Components route their state-changing fetch init objects through
// `withCsrf()`, which reads the meta tags at call time (not cached — Razor
// re-renders the token per request) and stamps the configured header onto
// POST / PUT / PATCH / DELETE.
//
// If the token meta tag is absent the helper degrades gracefully to a
// header-less request, so the kit keeps working against local-dev or
// non-CSRF backends.
//
// ---------------------------------------------------------------------------

/**
 * Read the CSRF token from `<meta name="csrf-token">`. Returns an empty
 * string if the tag is absent — callers must treat that as "skip the header"
 * rather than "send an empty header".
 */
export function getCsrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
}

/**
 * Read the CSRF header name from `<meta name="csrf-header">`. Defaults to
 * `RequestVerificationToken` (ASP.NET Core's convention) if the tag is absent.
 */
export function getCsrfHeaderName(): string {
    return document.querySelector('meta[name="csrf-header"]')?.getAttribute('content')
        ?? 'RequestVerificationToken';
}

/**
 * Wrap a `fetch` init with the CSRF header on unsafe verbs and ensure
 * `credentials: 'include'` is set so the session cookie travels with the
 * request. Components call this instead of constructing init objects by hand:
 *
 *     await fetch(url, withCsrf({ method: 'POST', body: '...' }));
 *
 * GET / HEAD / OPTIONS keep `credentials: 'include'` but never get the header.
 * If the token meta tag is absent the helper degrades gracefully to no header.
 */
export function withCsrf(init?: RequestInit): RequestInit {
    const method = (init?.method ?? 'GET').toUpperCase();
    if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') {
        return { ...init, credentials: 'include' };
    }
    const token = getCsrfToken();
    if (!token) {
        // Graceful degrade: backend isn't CSRF-protected, or the host page
        // hasn't emitted the meta tag. Don't send an empty header.
        return { ...init, credentials: 'include' };
    }
    const headerName = getCsrfHeaderName();
    return {
        ...init,
        credentials: 'include',
        headers: {
            ...init?.headers,
            [headerName]: token,
        },
    };
}
